import { useState } from "react";
import { Receipt, Play, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn } from "@/components/portal/ui";

// scope: "control" -> POST /control/billing/run ; "portal" -> POST /admin/billing/run
export function BillingRunner({ scope = "control", onDone }) {
  const { L } = useI18n();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const path = scope === "control" ? "/control/billing/run" : "/admin/billing/run";

  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post(`${path}?dry_run=${dry}`);
      const gen = scope === "control" ? data.totals.generated : data.generated;
      if (dry) {
        setPreview({ ...data, gen });
      } else {
        toast.success(L({
          de: `${gen} Rechnung(en) für ${data.period} erstellt`,
          fr: `${gen} facture(s) créée(s) pour ${data.period}`,
          it: `${gen} fattura/e create per ${data.period}`,
          en: `${gen} invoice(s) created for ${data.period}`,
        }));
        setPreview(null);
        onDone && onDone();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4" data-testid="billing-runner">
      <div className="text-sm font-semibold mb-1 flex items-center gap-2" style={{ color: "var(--text)" }}>
        <Receipt className="h-4 w-4" style={{ color: "var(--accent)" }} />
        {L({ de: "Monats-Rechnungen", fr: "Factures mensuelles", it: "Fatture mensili", en: "Monthly invoices" })}
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
        {L({
          de: "Erzeugt aus aktiven, kostenpflichtigen Abos je eine Rechnung pro Monat. Idempotent – keine Doppel.",
          fr: "Génère une facture par mois à partir des abonnements payants actifs. Idempotent – pas de doublons.",
          it: "Genera una fattura al mese dagli abbonamenti a pagamento attivi. Idempotente – nessun duplicato.",
          en: "Creates one invoice per month from active paid subscriptions. Idempotent – no duplicates.",
        })}
      </p>

      {preview && (
        <div className="mb-3 p-3 text-xs kw-fade" data-testid="billing-preview"
          style={{ background: "var(--surface-alt)", borderRadius: "var(--radius)", color: "var(--text)" }}>
          <div className="font-semibold mb-1">{L({ de: "Vorschau", fr: "Aperçu", it: "Anteprima", en: "Preview" })} · {preview.period}</div>
          <div style={{ color: "var(--text-muted)" }}>
            {L({ de: "Neu", fr: "Nouv.", it: "Nuove", en: "New" })}: <b style={{ color: "var(--text)" }}>{preview.gen}</b>
            {" · "}{L({ de: "übersprungen", fr: "ignorées", it: "saltate", en: "skipped" })}: {scope === "control" ? preview.totals.skipped : preview.skipped}
            {" · "}{L({ de: "gratis", fr: "gratuit", it: "gratis", en: "free" })}: {scope === "control" ? preview.totals.free_skipped : preview.free_skipped}
            {" · "}{L({ de: "Demo", fr: "démo", it: "demo", en: "demo" })}: {scope === "control" ? preview.totals.demo_skipped : preview.demo_skipped}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Btn variant="outline" className="h-9 px-3 text-xs" disabled={busy} data-testid="billing-preview-btn" onClick={() => run(true)}>
          <RotateCcw className="h-3.5 w-3.5" /> {L({ de: "Vorschau", fr: "Aperçu", it: "Anteprima", en: "Preview" })}
        </Btn>
        <Btn className="h-9 px-3 text-xs" disabled={busy} data-testid="billing-run-btn" onClick={() => run(false)}>
          <Play className="h-3.5 w-3.5" /> {L({ de: "Rechnungen generieren", fr: "Générer les factures", it: "Genera fatture", en: "Generate invoices" })}
        </Btn>
      </div>
    </Card>
  );
}
